/**
 * Drawer System
 * Handles opening, closing, and accessibility for all drawers
 * 
 * @requires ThemeUtils (utils.js) - optional, uses fallbacks if not available
 */

class Drawer {
  constructor() {
    this.drawers = document.querySelectorAll('.drawer');
    this.activeDrawer = null;
    this.previousActiveElement = null;
    this.focusTrapCleanup = null;
    
    this.init();
  }

  init() {
    if (this.drawers.length === 0) return;
    
    this.bindEvents();
  }

  bindEvents() {
    // Open drawer via data attributes
    document.addEventListener('click', (event) => {
      const toggleButton = event.target.closest('[data-drawer-toggle]');
      if (toggleButton) {
        event.preventDefault();
        const drawerId = toggleButton.getAttribute('data-drawer-toggle');
        const position = toggleButton.getAttribute('data-drawer-position') || 'right';
        this.openDrawer(drawerId, position);
      }
    });

    // Listen for custom open drawer events
    document.addEventListener('openDrawer', (event) => {
      const { drawerId, position = 'right' } = event.detail || {};
      if (drawerId) {
        this.openDrawer(drawerId, position);
      }
    });

    // Close drawer handlers
    document.addEventListener('click', (event) => {
      if (event.target.classList.contains('drawer-overlay') || 
          event.target.closest('.js-drawer-close')) {
        event.preventDefault();
        this.closeAllDrawers();
      }
    });

    // Close with ESC key
    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && this.activeDrawer) {
        this.closeAllDrawers();
      }
    });

    // Handle transition end for cleanup
    this.drawers.forEach(drawer => {
      drawer.addEventListener('transitionend', () => {
        if (drawer.getAttribute('aria-hidden') === 'true') {
          document.body.style.overflow = '';
        }
      });
    });
  }

  openDrawer(drawerId, position) {
    const drawer = document.querySelector(`[data-drawer-id="${drawerId}"]`);
    const overlay = document.querySelector(`[data-drawer-overlay="${drawerId}"]`);
    
    if (!drawer) {
      console.warn(`Drawer with id "${drawerId}" not found`);
      return;
    }
    
    // Close any open drawers first
    this.closeAllDrawers();
    
    // Store previous focus for restoration
    this.previousActiveElement = document.activeElement;
    
    // Open drawer
    drawer.setAttribute('aria-hidden', 'false');
    drawer.setAttribute('data-position', position);
    drawer.removeAttribute('inert');

    // Update Safari status/address bar color to match drawer background
    const themeColorMeta = document.querySelector('meta[name="theme-color"]');
    if (themeColorMeta) {
      this._previousThemeColor = themeColorMeta.getAttribute('content');
    }
    const drawerBg = getComputedStyle(document.documentElement).getPropertyValue('--color-drawer-background').trim();
    this._setThemeColor(drawerBg || '#ffffff');
    
    // Update body state
    document.body.style.overflow = 'hidden';
    document.body.classList.add('drawer-open');
    
    // Store active drawer reference
    this.activeDrawer = drawer;

    // Show overlay
    if (overlay) {
      overlay.classList.add('active');
    }
    
    // Set position class
    drawer.classList.remove('drawer--left', 'drawer--right');
    drawer.classList.add(`drawer--${position}`);

    // Setup focus trap
    this.setupFocusTrap(drawer);

    // Emit drawer open event
    this.dispatchEvent('drawerOpen', { drawerId, position });
  }

  closeDrawer(drawerId) {
    const drawer = document.querySelector(`[data-drawer-id="${drawerId}"]`);
    const overlay = document.querySelector(`[data-drawer-overlay="${drawerId}"]`);
    
    if (!drawer) return;
    
    // Cleanup focus trap
    if (this.focusTrapCleanup) {
      this.focusTrapCleanup();
      this.focusTrapCleanup = null;
    }
    
    // Restore focus
    if (drawer.contains(document.activeElement)) {
      const toggleButton = document.querySelector(`[data-drawer-toggle="${drawerId}"]`);
      if (toggleButton) {
        toggleButton.focus();
      } else if (this.previousActiveElement) {
        this.previousActiveElement.focus();
      }
    }
    
    // Close drawer
    drawer.setAttribute('aria-hidden', 'true');
    drawer.setAttribute('inert', '');
    
    // Hide overlay
    if (overlay) {
      overlay.classList.remove('active');
    }
    
    // Update body state
    document.body.style.overflow = '';
    document.body.classList.remove('drawer-open');

    // Restore Safari status/address bar color
    if (this._previousThemeColor) {
      this._setThemeColor(this._previousThemeColor);
    }
    
    this.activeDrawer = null;

    // Emit drawer close event
    this.dispatchEvent('drawerClose', { drawerId });
  }

  closeAllDrawers() {
    this.drawers.forEach(drawer => {
      const drawerId = drawer.getAttribute('data-drawer-id');
      if (drawerId) {
        this.closeDrawer(drawerId);
      }
    });
  }

  setupFocusTrap(drawer) {
    // Use ThemeUtils if available, otherwise simple focus management
    if (window.ThemeUtils?.trapFocus) {
      this.focusTrapCleanup = window.ThemeUtils.trapFocus(drawer);
    } else {
      // Simple focus to first focusable element
      const firstFocusable = drawer.querySelector(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
      );
      if (firstFocusable) {
        setTimeout(() => firstFocusable.focus(), 100);
      }
    }
  }

  _setThemeColor(color) {
    // Remove and recreate meta tag to force Safari to update
    const existing = document.querySelector('meta[name="theme-color"]');
    if (existing) existing.remove();
    const meta = document.createElement('meta');
    meta.name = 'theme-color';
    meta.content = color;
    document.head.appendChild(meta);
  }

  dispatchEvent(eventName, detail) {
    if (window.ThemeUtils?.dispatchEvent) {
      window.ThemeUtils.dispatchEvent(eventName, detail);
    } else {
      document.dispatchEvent(new CustomEvent(eventName, { detail, bubbles: true }));
    }
  }
}

// Initialize when DOM is ready
document.addEventListener('DOMContentLoaded', () => {
  window.drawer = new Drawer();
});